import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import NoData from '../dashboard/noData';
import { LanguageEmpty } from '../icons';
import { useEffect, useMemo, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { IMatterRequest } from '../../interface/matterRequest';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useFetchLanguages } from '../../hooks/useFetchLanguages';
import { ILanguage } from '../../interface/languages';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { useQueryClient } from '@tanstack/react-query';

const SelectLanguage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setTitle } = usePage();
  const { language: appLanguage } = useLanguage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: languageList,
    isLoading: isLanguagesLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchLanguages({
    pageSize: 100,
    search: searchValue,
  });
  const refreshLanguageList = () => {
    queryClient.removeQueries({ queryKey: ['languages'] });
    return refetch();
  };
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    // manageMatterRequest.tsx has no discard model — this mirrors its own handleBack
    // (reset + leave). When embedded in the mail flow (transactionMail.tsx) it's not a
    // separate screen on the stack, so only one level needs to be skipped there vs two
    // for the standalone create/edit matter request route.
    updateManageMatterRequest(null, true);
    goBack(undefined, matterRequest?.isFromMailOrAppointment ? 1 : 2);
  };
  const languageSortOptions: ISortOption[] = useMemo(
    () => [{ field: 'name', label: t('common:language'), type: 'text', defaultDirection: 'ASC' }],
    [t],
  );
  const [languageActiveSort, setLanguageActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });
  const sortedLanguageList = useMemo(
    () =>
      languageList
        ? [...languageList].sort((a, b) => {
            const comparison = getTranslationFromTranslatableEntity(
              a.translations,
              appLanguage,
            ).localeCompare(
              getTranslationFromTranslatableEntity(b.translations, appLanguage),
              appLanguage,
            );
            return languageActiveSort.direction === 'DESC' ? -comparison : comparison;
          })
        : languageList,
    [languageList, appLanguage, languageActiveSort.direction],
  );
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && languageList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('common:selectLanguage')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin data-testid="small-indicator" size="small" />;
    }
    return null;
  };
  const handleSelectLanguage = (language: ILanguage) => {
    updateManageMatterRequest({
      ...matterRequest,
      language: language?.languageCode,
      isLanguageSetByUser: true,
    } as unknown as IMatterRequest);
    goBackWithSelection();
  };
  return (
    <CollapsibleListLayout
      testId="language-selection"
      header={
        <SearchInputWithFilters
          searchTitle={t('common:searchLanguages')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={refreshLanguageList}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={languageSortOptions}
            activeSort={languageActiveSort}
            onApply={setLanguageActiveSort}
          />
        </SearchInputWithFilters>
      }
    >
      {sortedLanguageList && sortedLanguageList.length > 0 && (
        <RadioGroup
          data-testid="radio-group"
          className="d-flex fd-column gap-10"
          value={matterRequest?.language}
        >
          {sortedLanguageList.map((language: ILanguage) => (
            <Radio
              key={language.languageCode}
              data-testid={`matter-request-language-radio-${language?.languageCode}`}
              value={language.languageCode}
              onClick={() => handleSelectLanguage(language)}
            >
              <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
                {getTranslationFromTranslatableEntity(language.translations, appLanguage)}
              </Text>
            </Radio>
          ))}
        </RadioGroup>
      )}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!sortedLanguageList ||
        (Object.entries(sortedLanguageList).length === 0 && !isLanguagesLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('common:noLanguages')} icon={<LanguageEmpty />} />
        </div>
      )}
      {isLanguagesLoading ? <SkeletonOptionList /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default SelectLanguage;
